import { useEffect, useState } from 'react'
import 'vanilla-cookieconsent/dist/cookieconsent.css'
import * as CookieConsent from 'vanilla-cookieconsent'
import type { CookieConsentConfig } from 'vanilla-cookieconsent'

export const deleteCookie = (name: string, path = '/') => {
  document.cookie = `${name}=; Path=${path}; Expires=Thu, 01 Jan 1970 00:00:00 GMT`
}

export const deleteCookiesByRegex = (
  regex: RegExp,
  path = '/'
) => {
  if (typeof document === 'undefined') {
    return
  }

  // Avoid issues when regex has the global `g` flag.
  const safeFlags = regex.flags.replace('g', '')
  const safeRegex = new RegExp(regex.source, safeFlags)

  const allCookies = document.cookie ? document.cookie.split(';') : []
  allCookies.forEach(cookiePart => {
    const trimmed = cookiePart.trim()
    if (!trimmed) return

    const name = trimmed.split('=')[0]
    if (safeRegex.test(name)) {
      deleteCookie(name, path)
    }
  })
}

export const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') {
    return null
  }
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift()
    return cookieValue ?? null
  }
  return null
}

// Marketing: Meta, TikTok, LinkedIn, Google Ads — patterns to clear when consent revoked
const MARKETING_COOKIE_REGEX =
  /^(_fbp|_fbc|_ttp|lidc|li_gc|li_sugr|li_fat_id|li_mc|UserMatchHistory|AnalyticsSyncHistory|lms_analytics|lms_ads|bcookie)$/
const GOOGLE_ADS_COOKIE_PREFIX = /^_gcl_/

const clearMarketingCookies = () => {
  deleteCookiesByRegex(MARKETING_COOKIE_REGEX)
  deleteCookiesByRegex(GOOGLE_ADS_COOKIE_PREFIX)
}

const pluginConfig = ({
  setShouldShowButton,
}: {
  setShouldShowButton: React.Dispatch<React.SetStateAction<boolean>>
}): CookieConsentConfig => ({
  autoShow: true,
  hideFromBots: false,
  guiOptions: {
    consentModal: {
      layout: 'box',
      position: 'bottom right',
      equalWeightButtons: true,
      flipButtons: false,
    },
    preferencesModal: {
      layout: 'box',
      position: 'left',
      equalWeightButtons: true,
      flipButtons: false,
    },
  },

  onFirstConsent: function ({ cookie }) {
    if (!cookie.categories.includes('analytics')) {
      deleteCookiesByRegex(/^(_ga|_gid)/)
    } else {
      // Reload to init GA
      window.location.reload()
    }
    if (!cookie.categories.includes('media')) {
      deleteCookie('vuid')
    }
    if (!cookie.categories.includes('marketing')) {
      clearMarketingCookies()
    }
  },

  onConsent: function () {
    const cookie = CookieConsent.getCookie()
    const allCategories = Object.keys(cookie.categories)
    const acceptedCategories = cookie.categories

    if (acceptedCategories.length === 0) {
      setShouldShowButton(true)
      return
    }

    const hasAcceptedAll = allCategories.every((cat) =>
      acceptedCategories.includes(cat)
    )
    setShouldShowButton(!hasAcceptedAll)
  },

  onChange: ({ cookie, changedCategories }) => {
    if (!cookie.categories.includes('analytics')) {
      deleteCookiesByRegex(/^(_ga|_gid)/)
    } else if (changedCategories.includes('analytics')) {
      // Reload to init GA
      window.location.reload()
    }

    if (!cookie.categories.includes('media')) {
      deleteCookie('vuid')
    }

    if (!cookie.categories.includes('marketing')) {
      clearMarketingCookies()
    }
  },

  categories: {
    analytics: {
      autoClear: {
        cookies: [
          {
            name: /^(_ga|_gid)/,
          },
        ],
      },
    },
    media: {
      enabled: false,
      readOnly: false,
    },
    marketing: {
      enabled: false,
      readOnly: false,
      autoClear: {
        cookies: [
          {
            name: MARKETING_COOKIE_REGEX,
          },
          {
            name: GOOGLE_ADS_COOKIE_PREFIX,
          },
        ],
      },
    },
  },

  language: {
    default: 'en',
    translations: {
      en: {
        consentModal: {
          title: 'Cookie preferences',
          description:
            'We use necessary technologies, such as cookies, to store and/or access device information to provide our website. By clicking “Accept all” you also consent to our use of technologies and the processing of your personal data to analyse usage. You can learn more by clicking “View preferences” and you can withdraw or manage your consent at any time by the visiting “Manage Consent” toggle on our website.',
          acceptAllBtn: 'Accept all',
          acceptNecessaryBtn: 'Deny all',
          showPreferencesBtn: 'View preferences',
          footer: `
            <a href="/privacy-policy">Privacy Policy</a>
          `,
        },
        preferencesModal: {
          title: 'Cookie preferences',
          acceptAllBtn: 'Accept all',
          acceptNecessaryBtn: 'Reject all',
          savePreferencesBtn: 'Save preferences',
          closeIconLabel: 'Close',
          sections: [
            {
              title: 'Cookie Usage',
              description:
                'To provide the best experiences, we use technologies like cookies to store and/or access device information. Consenting to these technologies will allow us to process data such as browsing behavior or unique IDs on this site. Not consenting or withdrawing consent, may adversely affect certain features and functions.',
            },
            {
              title: 'Analytics cookies',
              linkedCategory: 'analytics',
              cookieTable: {
                headers: {
                  name: 'Name',
                  domain: 'Service',
                  description: 'Description',
                  expiration: 'Expiration',
                },
                body: [
                  {
                    name: '_ga',
                    domain: 'Google Analytics',
                    description:
                      'Cookie set by <a href="https://marketingplatform.google.com/about/analytics/" target="_blank" rel="noopener noreferrer">Google Analytics</a>.',
                    expiration: 'Expires after 1 year',
                  },
                ],
              },
            },
            {
              title: 'Embedded videos (Vimeo)',
              description:
                'We use embedded video players (e.g. Vimeo) to display media content. These services may set cookies to track usage.',
              linkedCategory: 'media',
              cookieTable: {
                headers: {
                  name: 'Name',
                  domain: 'Service',
                  description: 'Description',
                  expiration: 'Expiration',
                },
                body: [
                  {
                    name: 'vuid',
                    domain: 'Vimeo',
                    description:
                      'This cookie is used by Vimeo to collect tracking information.',
                    expiration: '1 years',
                  },
                ],
              },
            },
            {
              title: 'Marketing / Advertising',
              description:
                'We use Google Ads, Meta Pixel (Facebook), TikTok Pixel, and LinkedIn Insight Tag to measure ad performance and website usage. These services may set cookies to track usage.',
              linkedCategory: 'marketing',
              cookieTable: {
                headers: {
                  name: 'Name',
                  domain: 'Service',
                  description: 'Description',
                  expiration: 'Expiration',
                },
                body: [
                  {
                    name: '_gcl_*',
                    domain: 'Google Ads',
                    description:
                      'Cookies such as _gcl_au set by Google Ads / gtag for conversion measurement and remarketing.',
                    expiration: 'Varies (often up to 90 days)',
                  },
                  {
                    name: '_fbp, _fbc',
                    domain: 'Meta (Facebook)',
                    description:
                      'Cookies used by Meta Pixel to deliver, measure, and improve advertising and to understand user behavior.',
                    expiration: 'Expires after 3 months',
                  },
                  {
                    name: '_ttp',
                    domain: 'TikTok',
                    description:
                      'Cookie set by TikTok Pixel to measure ad performance and website usage.',
                    expiration: 'Expires after 3 months',
                  },
                  {
                    name: 'lidc, li_gc, bcookie',
                    domain: 'LinkedIn',
                    description:
                      'Cookies used by LinkedIn Insight Tag for advertising and analytics.',
                    expiration: 'Varies (typically up to 1 year)',
                  },
                ],
              },
            },
          ],
        },
      },
    },
  },
})

const CookieConsentComponent = () => {
  const [shouldShowButton, setShouldShowButton] = useState(false)

  useEffect(() => {
    // Initialize cookie consent
    setTimeout(() => {
      const cookieRaw = getCookie('cc_cookie')
      const cookie = cookieRaw
        ? JSON.parse(decodeURIComponent(cookieRaw))
        : null

      if (!cookie) {
        setShouldShowButton(true)
      } else {
        const allCategories = ['analytics', 'media', 'marketing']
        const acceptedCategories = cookie.categories

        const hasAcceptedAll = allCategories.every((cat) =>
          acceptedCategories.includes(cat)
        )

        setShouldShowButton(!hasAcceptedAll)
      }
    }, 300)
  }, [])

  useEffect(() => {
    CookieConsent.run(pluginConfig({ setShouldShowButton }))
  }, [shouldShowButton])


  return (
    <button
      type="button"
      className="fixed bottom-0 right-4 bg-white z-[9999] cursor-pointer px-4 pt-1 rounded-t-[16px] font-secondary shadow-base"
      data-cc="show-consentModal"
    >
      Cookie preferences
    </button>
  )
}

export default CookieConsentComponent
